/**
 * Attaches training employee names to R0003 report rows.
 *
 * The names are display-only decoration for the Notes cell. They are kept on
 * their own row fields (never inside `notes`) so that the note dialog and the
 * store-notes mutation, which read and write `notes` alone, cannot pick them up.
 *
 * @module trainingNames
 */

import { DAYS_OF_WEEK, NUM_DATA_ROWS } from './mergeMetrics/constants'

/**
 * Maps the report's abbreviated day labels to the full day names used by the
 * training data, which stores a day-of-week name rather than a date.
 */
export const DAY_ABBR_TO_FULL = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday'
}

const EMPTY_NAMES = { foh: [], boh: [] }

/**
 * Normalizes a store name for matching. Both sides use the store shortName, so
 * this only absorbs casing and whitespace drift between the two data sources.
 *
 * @param {string} storeName - Store shortName
 * @returns {string} Normalized key, or '' when the name is unusable
 */
const normalizeStoreName = (storeName) =>
  typeof storeName === 'string' ? storeName.trim().toLowerCase() : ''

/**
 * Sets the display-only name fields on one day row.
 *
 * @param {Object} row - Day row from the report data
 * @param {Object} names - `{ foh: string[], boh: string[] }`
 * @returns {void}
 */
const setRowNames = (row, names) => {
  if (!row) {
    return
  }
  row.trainingNamesFOH = names.foh
  row.trainingNamesBOH = names.boh
}

/**
 * Attaches training employee names to each store's Mon–Sun rows.
 *
 * Mutates and returns `mergedData`, which is already a deep clone by the time it
 * reaches here (see `mergeMetrics`). Every day row receives arrays — empty when
 * there was no training — so the row-height calculator never sees `undefined`.
 *
 * The trailing area-rollup block is intentionally left without names: it sums
 * several stores, so there is no single store to match, and listing an entire
 * area's trainees would swamp the cell.
 *
 * @param {Array} mergedData - Report data: `[{ area, data: [row, ...] }]`
 * @param {Object} namesByStoreDay - `{ [store]: { [dayName]: { foh, boh } } }`
 *   from `fetchTrainingNamesForWeek`
 * @returns {Array} The same `mergedData` reference
 *
 * @example
 * attachTrainingNames(mergedMetrics, names);
 * // mergedMetrics[0].data[1].trainingNamesFOH -> ['hsolo']
 */
export const attachTrainingNames = (mergedData, namesByStoreDay) => {
  if (!Array.isArray(mergedData)) {
    return mergedData
  }

  // Index by normalized store name once, rather than per store block.
  const namesIndex = new Map()
  if (namesByStoreDay && typeof namesByStoreDay === 'object') {
    for (const [storeName, dayMap] of Object.entries(namesByStoreDay)) {
      const key = normalizeStoreName(storeName)
      if (key) {
        namesIndex.set(key, dayMap)
      }
    }
  }

  for (const areaData of mergedData) {
    const sectionData = areaData?.data

    if (!Array.isArray(sectionData)) {
      continue
    }

    const rollupIndex = sectionData.length - NUM_DATA_ROWS

    if (rollupIndex < 0) {
      continue
    }

    // Store blocks: rows [i] store header, [i+1..i+7] Mon–Sun, then totals rows.
    for (let i = 0; i < rollupIndex; i += NUM_DATA_ROWS) {
      const dayMap = namesIndex.get(normalizeStoreName(sectionData[i]?.day))

      DAYS_OF_WEEK.forEach((dayAbbr, dayIndex) => {
        const names = dayMap?.[DAY_ABBR_TO_FULL[dayAbbr]] || EMPTY_NAMES
        setRowNames(sectionData[i + dayIndex + 1], {
          foh: names.foh || [],
          boh: names.boh || []
        })
      })
    }

    // Area rollup day rows carry hours only, but still need the fields present.
    DAYS_OF_WEEK.forEach((dayAbbr, dayIndex) => {
      setRowNames(sectionData[rollupIndex + dayIndex + 1], { foh: [], boh: [] })
    })
  }

  return mergedData
}
